import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Home Electrical Safety Inspection Services',
  provider: {
    '@type': 'ElectricalContractor',
    name: 'Electrician Solutions Fresno',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Fresno',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Fresno',
    '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
  },
  description:
    'Professional home electrical safety inspections in Fresno CA including code compliance checks, panel inspections, wiring assessments, pre-purchase inspections, and detailed safety reports by licensed C-10 electricians',
  url: 'https://electriciansolutionsfresno.com/services/residential-electrical/home-electrical-inspection',
};

export default function HomeElectricalInspectionPage() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Comprehensive Safety Inspections',
      description:
        'Complete electrical safety inspections covering panels, wiring, outlets, switches, lighting, grounding, and all electrical systems in your Fresno home with detailed reports',
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Pre-Purchase Home Inspections',
      description:
        'Thorough electrical inspections for home buyers — identify safety hazards, code violations, and potential repair costs before purchasing a Fresno property',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Code Compliance Assessments',
      description:
        'Verify your Fresno home electrical system meets California Title 24 codes, NEC standards, and local building requirements — essential for home sales and insurance',
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Electrical Panel Inspections',
      description:
        'Detailed panel inspections checking for proper sizing, circuit breaker condition, wiring connections, grounding, and safety hazards in Fresno home electrical panels',
    },
    {
      icon: 'ri-home-wifi-line',
      title: 'Wiring System Assessments',
      description:
        'Inspect home wiring for outdated systems (knob-and-tube, aluminum), damage, improper installations, and safety hazards throughout Fresno properties',
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Safety Hazard Identification',
      description:
        'Identify electrical safety hazards including overloaded circuits, improper grounding, missing GFCI protection, fire risks, and code violations in Fresno homes',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Inspection Reports',
      description:
        'Receive comprehensive written reports documenting all findings, safety concerns, code violations, and recommended repairs with photos and cost estimates for Fresno homes',
    },
    {
      icon: 'ri-tools-line',
      title: 'Repair Recommendations',
      description:
        'Get expert recommendations for necessary repairs, upgrades, and improvements to ensure your Fresno home electrical system is safe, code-compliant, and reliable',
    },
  ];

  const whatsIncluded = [
    'Complete electrical system inspection by licensed C-10 electricians',
    'Electrical panel assessment including capacity, condition, and safety',
    'Wiring system inspection for outdated or dangerous wiring types',
    'Outlet and switch testing including GFCI and AFCI protection verification',
    'Grounding and bonding system inspection for proper electrical safety',
    'Smoke detector and carbon monoxide detector inspection and testing',
    'Detailed written report with photos, findings, and repair recommendations',
    'Cost estimates for recommended repairs and upgrades for Fresno homes',
  ];

  return (
    <>
      <SEOHead
        title="Home Electrical Safety Inspection Fresno CA | Electrical Inspection"
        description="Professional home electrical safety inspections in Fresno, CA. Code compliance checks, panel inspections, wiring assessments, pre-purchase inspections, detailed reports. Licensed C-10 electricians. Free quotes."
        canonical="/services/residential-electrical/home-electrical-inspection"
        keywords="home electrical inspection Fresno, electrical safety inspection Fresno CA, pre-purchase electrical inspection Fresno, electrical code compliance Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Home Electrical Safety Inspections — Fresno, CA"
        heroSubtitle="Comprehensive electrical inspections for safety, code compliance & peace of mind"
        heroImage="https://public.readdy.ai/ai/img_res/612ebf5e6cd5b6962d2e417189091cd9.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrician%20hands%20inspecting%20electrical%20panel%20with%20testing%20equipment%20and%20writing%20notes%20on%20inspection%20report%20clipboard%20bright%20indoor%20lighting%20California%20home&width=700&height=600&seq=inspect-fresno-panel-07&orientation=portrait"
        tagline="Trusted by 500+ Fresno Homeowners"
        fresnoContent="Protecting Fresno homeowners with professional electrical safety inspections that identify hazards and ensure code compliance. Whether you're buying a home in Tower District and need a pre-purchase inspection, selling a property in Woodward Park and need code compliance verification, concerned about electrical safety in an older Fig Garden home, or simply want peace of mind about your Clovis home electrical system, our licensed C-10 electricians provide comprehensive inspections with detailed reports. Many Fresno homes have outdated electrical systems, code violations, or hidden safety hazards that can cause fires, shocks, or equipment damage. Our thorough inspections cover electrical panels, wiring systems, outlets, switches, grounding, GFCI/AFCI protection, and all electrical components."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'Why do I need a home electrical inspection in Fresno?',
            answer:
              'Electrical inspections identify safety hazards, code violations, and potential problems before they cause fires, shocks, or equipment damage. Inspections are essential when buying or selling a home, after electrical work by previous owners, in older homes with outdated systems, or if you notice warning signs like flickering lights, tripped breakers, or burning smells.',
          },
          {
            question: 'How much does a home electrical inspection cost in Fresno?',
            answer:
              "Home electrical inspections typically cost $200–$400 for a standard residential property, depending on home size and complexity. Pre-purchase inspections for home buyers cost $250–$500 and include detailed reports suitable for real estate transactions. Considering that electrical inspections can identify thousands of dollars in hidden problems and prevent dangerous safety hazards, they're an excellent investment.",
          },
          {
            question: 'What does a home electrical inspection include in Fresno?',
            answer:
              'Our comprehensive electrical inspections include: electrical panel assessment (capacity, condition, proper installation), circuit breaker testing, wiring system inspection (checking for outdated or dangerous wiring), outlet and switch testing, GFCI and AFCI protection verification, grounding and bonding inspection, smoke and CO detector testing, lighting fixture inspection, and identification of code violations and safety hazards.',
          },
          {
            question: 'How long does a home electrical inspection take in Fresno?',
            answer:
              "Most home electrical inspections take 2-4 hours depending on home size and electrical system complexity. Smaller homes (under 1,500 sq ft) typically take 2-3 hours. Larger homes (2,500+ sq ft) or homes with multiple panels may take 4-6 hours. You'll receive your detailed written report within 24-48 hours after the inspection.",
          },
          {
            question: 'Should I get an electrical inspection when buying a Fresno home?',
            answer:
              "Absolutely. Pre-purchase electrical inspections are essential for home buyers. General home inspectors provide basic electrical checks, but licensed electricians provide much more detailed assessments. We identify hidden problems like outdated wiring, undersized panels, code violations, and safety hazards that could cost thousands to repair. Many Fresno homes, especially older properties in Tower District and Fig Garden, have electrical issues that aren't obvious during casual viewing.",
          },
          {
            question: 'What are common electrical problems found in Fresno homes?',
            answer:
              'Common electrical problems in Fresno homes include: outdated 100-amp panels insufficient for modern loads, knob-and-tube or aluminum wiring in older homes, missing GFCI protection in kitchens and bathrooms, overloaded circuits, improper DIY electrical work, Federal Pacific or Zinsco panels (fire hazards), inadequate grounding, missing smoke and CO detectors, and code violations from unpermitted work.',
          },
        ]}
        serviceSlug="home-electrical-inspection"
      />
    </>
  );
}
